import { cn } from '@/lib/utils';
import type { SacramentStatus } from 'generated/prisma/enums';
import { useState, type ReactNode } from 'react';

export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('text-[11px] font-bold tracking-[.09em] text-brand', className)}>
      {children}
    </span>
  );
}

/** Label de 10–11px em caixa alta, cor muted. */
export function Label({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('text-[11px] font-bold tracking-[.09em] text-muted', className)}>
      {children}
    </span>
  );
}

interface PageHeaderProps {
  eyebrow: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}

export function PageHeader({ eyebrow, title, description, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-5">
      <div className="flex flex-col gap-2">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="m-0 font-display text-[34px] leading-[1.1] font-extrabold tracking-[-0.03em] text-ink">
          {title}
        </h1>
        {description && <p className="m-0 text-[15px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
    </div>
  );
}

export function Panel({
  children,
  className,
  ...props
}: React.ComponentProps<'section'>) {
  return (
    <section
      className={cn('min-w-0 rounded-[18px] border border-line bg-surface', className)}
      {...props}
    >
      {children}
    </section>
  );
}

export function PanelTitle({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-display text-lg font-bold text-ink">{title}</span>
      {description && (
        <span className="text-[13px] leading-normal text-muted">{description}</span>
      )}
    </div>
  );
}

export type Tone = 'ok' | 'warn' | 'neutral' | 'brand';

const toneClass: Record<Tone, string> = {
  ok: 'bg-ok-tint text-ok',
  warn: 'bg-warn-tint text-warn',
  neutral: 'bg-surface-2 text-muted',
  brand: 'bg-brand text-white',
};

export function Pill({
  tone = 'neutral',
  children,
  className,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap',
        toneClass[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function InitialsAvatar({
  text,
  src,
  className,
}: {
  text: string;
  /** Foto do usuário; se faltar ou não carregar, mostra as iniciais. */
  src?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = !!src && failed !== src;

  return (
    <span
      className={cn(
        'flex size-9 flex-none items-center justify-center overflow-hidden rounded-full bg-brand-tint font-display text-xs font-bold text-brand',
        className,
      )}
    >
      {showImage ? (
        <img src={src} alt="" className="size-full object-cover" onError={() => setFailed(src)} />
      ) : (
        text
      )}
    </span>
  );
}

const SACRAMENT_TEXT: Record<SacramentStatus, string> = {
  BOTH: 'ambos',
  ONLY_ONE: 'somente um',
  NONE: 'nenhum',
};

const sacramentTone = (s: SacramentStatus): Tone =>
  s === 'BOTH' ? 'ok' : s === 'ONLY_ONE' ? 'warn' : 'neutral';

interface SacramentInfo {
  baptismStatus: SacramentStatus;
  confirmationStatus: SacramentStatus;
  marriageSacrament: boolean;
}

/** Tags B / C / M — verde = ambos/sim, âmbar = somente um, neutro = nenhum/não. */
export function SacramentTags({ couple }: { couple: SacramentInfo }) {
  return (
    <div className="flex gap-1">
      <Pill
        className="px-2 py-[3px]"
        tone={sacramentTone(couple.baptismStatus)}
        title={`Batismo: ${SACRAMENT_TEXT[couple.baptismStatus]}`}
      >
        B
      </Pill>
      <Pill
        className="px-2 py-[3px]"
        tone={sacramentTone(couple.confirmationStatus)}
        title={`Crisma: ${SACRAMENT_TEXT[couple.confirmationStatus]}`}
      >
        C
      </Pill>
      <Pill
        className="px-2 py-[3px]"
        tone={couple.marriageSacrament ? 'ok' : 'neutral'}
        title={couple.marriageSacrament ? 'Casados na Igreja' : 'Não casados na Igreja'}
      >
        M
      </Pill>
    </div>
  );
}

export function StatusPill({ active }: { active: boolean }) {
  return (
    <Pill tone={active ? 'ok' : 'neutral'} className="justify-self-start">
      {active ? 'Ativo' : 'Inativo'}
    </Pill>
  );
}

export function DashedNote({
  icon,
  children,
  className,
}: {
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-[14px] border border-dashed border-line-2 p-4',
        className,
      )}
    >
      <span className="flex pt-px text-brand [&_svg]:size-[18px]">{icon}</span>
      <span className="text-[13px] leading-[1.55] text-body">{children}</span>
    </div>
  );
}

export function BrandMark({
  subtitle,
  className,
}: {
  subtitle: string;
  className?: string;
}) {
  return (
    <span className={cn('flex items-center gap-3 text-ink', className)}>
      <img
        src="/assets/logo.png"
        alt="Comunidade Católica Apóstolos"
        className="logo-img h-9 w-auto"
      />
      <span className="flex flex-col gap-0.5">
        <span className="font-display text-lg leading-none font-extrabold tracking-[-0.02em]">
          Enchei-vos
        </span>
        <span className="text-[10px] leading-none font-bold tracking-[.12em] text-muted">
          {subtitle}
        </span>
      </span>
    </span>
  );
}
